/* ═══════════════════════════════════════════════════════
   Monmouth Cards — Sealed Boxes (Wax) Collection Filters (Set)
   assets/monmouth-sealed-filters.js

   Set: search + autocomplete + removable chips, multi-select — same
   pattern as Team in monmouth-singles-filters.js (173 distinct set
   values is too many for a checkbox list, and it's a "Multi-select"
   control per the Filter Architecture doc, same as Brand/Format/Box
   Type). Picking a suggestion adds a chip and feeds the shared
   multi-select OR merge engine in monmouth-collection.js, so "Chrome or
   Prizm" works the same way "PSA or SGC" does on the Singles side.

   Backed by monmouth-sealed-filters.json (set names pulled from
   retag_sealed_log.jsonl, the GPT tagging run for wax/sealed products).

   Re-entrant: window.MC.initSealedFilters() is called both on first
   page load and after every AJAX section-replace (monmouth-collection.js),
   since that swap destroys and recreates this markup from scratch.
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var filterDataPromise = null;
  /* Real fix (player-autosuggest-race-condition-and-brand-filter-merge-
     prompt.md, Bug 1 — "same loading pattern" this file shares with
     monmouth-singles-filters.js's Player/Team search) — tracks whether
     the fetch has actually resolved yet, so the input handler below can
     show a real "Loading…" row instead of jumping straight to "No sets
     found" on a genuinely cold first load. */
  var filterDataReady = false;

  function loadFilterData() {
    if (filterDataPromise) return filterDataPromise;
    var url = window.MC_SEALED_FILTER_DATA_URL;
    if (!url) {
      filterDataReady = true;
      return Promise.resolve({ sets: [] });
    }
    filterDataPromise = fetch(url)
      .then(function (r) { return r.json(); })
      .catch(function (err) {
        console.warn('[MC] Failed to load sealed filter data:', err);
        return { sets: [] };
      });
    filterDataPromise.then(function () { filterDataReady = true; });
    return filterDataPromise;
  }

  /* Reuse the shared copy monmouth-collection.js exposes rather than
     duplicating it (must run after monmouth-collection.js — see the
     script-order comment in sections/monmouth-collection-filters.liquid). */
  function handleize(str) {
    if (window.MC && typeof window.MC.handleize === 'function') return window.MC.handleize(str);
    return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  /* Same "click outside closes the dropdown" pattern as
     monmouth-singles-filters.js, kept separate since this file can load
     independently of that one (Singles vs Sealed Boxes pages never
     overlap, but neither file should assume the other ran). */
  function installGlobalDismissListener() {
    if (window._mcSealedDismissInstalled) return;
    window._mcSealedDismissInstalled = true;
    document.addEventListener('click', function (e) {
      var box = document.getElementById('mc-set-suggestions');
      var input = document.getElementById('mc-set-search');
      if (box && input && !box.contains(e.target) && e.target !== input) {
        box.hidden = true;
      }
    });
  }

  /* ══════════════════════════════════════════════
     SET — search + autocomplete + multi-select chips
  ══════════════════════════════════════════════ */
  function renderSetChips() {
    var container = document.getElementById('mc-set-chips');
    if (!container) return;
    var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
    var selected = (state && state.multi && state.multi.set) || [];

    container.innerHTML = selected.map(function (setName) {
      return '<button type="button" class="mc-sidebar__chip" data-set-chip="1">' +
        escapeHtml(setName) +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>';
    }).join('');

    Array.from(container.children).forEach(function (chip, i) {
      chip.addEventListener('click', function () {
        if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(selected[i], 'set');
      });
    });
  }

  function initSetSearch() {
    var input = document.getElementById('mc-set-search');
    var box = document.getElementById('mc-set-suggestions');

    if (input && box) {
      var currentMatches = [];
      var activeIndex = -1;

      function renderSuggestions(matches, query) {
        currentMatches = matches;
        activeIndex = -1;
        if (!matches.length) {
          box.innerHTML = query
            ? '<div class="mc-sidebar__suggestion mc-sidebar__suggestion--empty">No sets found</div>'
            : '';
          box.hidden = !query;
          return;
        }
        box.innerHTML = matches.map(function (name, i) {
          return '<button type="button" class="mc-sidebar__suggestion" data-index="' + i + '">' + escapeHtml(name) + '</button>';
        }).join('');
        box.hidden = false;
      }

      function highlight(items) {
        items.forEach(function (el, i) {
          el.classList.toggle('mc-sidebar__suggestion--active', i === activeIndex);
        });
      }

      function choose(name) {
        if (!name) return;
        input.value = '';
        box.hidden = true;
        renderSuggestions([], '');
        if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(name, 'set');
      }

      input.addEventListener('input', function () {
        var query = input.value.trim().toLowerCase();
        if (!query) { renderSuggestions([], ''); return; }
        // Real fix (player-autosuggest-race-condition-and-brand-filter-
        // merge-prompt.md, Bug 1) — same fetch-in-flight race condition
        // as Player/Team search: show a real "Loading…" row instead of
        // silently doing nothing until the fetch resolves, and re-filter
        // against input.value fresh at resolve time (never trust this
        // closure's own `query` — the visitor may have typed further or
        // deleted characters while the fetch was still in flight).
        if (!filterDataReady) {
          box.innerHTML = '<div class="mc-sidebar__suggestion mc-sidebar__suggestion--empty">Loading…</div>';
          box.hidden = false;
        }
        loadFilterData().then(function (data) {
          var liveQuery = input.value.trim().toLowerCase();
          if (!liveQuery) { renderSuggestions([], ''); return; }
          var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
          var selected = (state && state.multi && state.multi.set) || [];
          var selectedHandles = selected.map(handleize);
          var matches = (data.sets || [])
            .filter(function (name) {
              return name.toLowerCase().indexOf(liveQuery) !== -1 && selectedHandles.indexOf(handleize(name)) === -1;
            })
            .slice(0, 8);
          renderSuggestions(matches, liveQuery);
        });
      });

      box.addEventListener('click', function (e) {
        var btn = e.target.closest('.mc-sidebar__suggestion');
        if (!btn || btn.classList.contains('mc-sidebar__suggestion--empty')) return;
        choose(currentMatches[parseInt(btn.dataset.index, 10)]);
      });

      input.addEventListener('keydown', function (e) {
        if (box.hidden || !currentMatches.length) return;
        var items = box.querySelectorAll('.mc-sidebar__suggestion:not(.mc-sidebar__suggestion--empty)');
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          activeIndex = Math.min(activeIndex + 1, items.length - 1);
          highlight(items);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          activeIndex = Math.max(activeIndex - 1, 0);
          highlight(items);
        } else if (e.key === 'Enter') {
          if (activeIndex >= 0) {
            e.preventDefault();
            choose(currentMatches[activeIndex]);
          }
        } else if (e.key === 'Escape') {
          box.hidden = true;
        }
      });

      installGlobalDismissListener();
    }

    renderSetChips();
  }

  function init() {
    initSetSearch();
  }

  window.MC = window.MC || {};
  window.MC.initSealedFilters = init;
  window.MC.renderSetChips = renderSetChips;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
